import { Content, Header, Page } from '@backstage/core-components';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LinkIcon from '@mui/icons-material/Link';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { COLORS, Field, StatusChip } from '../argocd/ui';
import { useUrlState } from '../shared/useUrlState';
import { useOpenBaoApi } from './api';
import { PoliciesView } from './PoliciesView';
import { SecretsView, type SecretsLocation } from './SecretsView';
import {
  OPENBAO_NAVIGATION_KEYS,
  parseOpenBaoUrl,
  serializeOpenBaoUrl,
  type OpenBaoTab,
  type OpenBaoUrlState,
} from './openbaoUrl';
import type { BaoAuthMount, BaoSession, BaoStatus } from './types';

function AccessView({
  authMounts,
  error,
}: {
  authMounts: BaoAuthMount[] | null;
  error: string | null;
}) {
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!authMounts) return <CircularProgress size={22} />;
  return (
    <TableContainer
      sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2 }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Path</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Accessor</TableCell>
            <TableCell>Description</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {authMounts.map(mount => (
            <TableRow key={mount.path}>
              <TableCell sx={{ fontSize: 12.5, fontWeight: 600 }}>
                {mount.path}
              </TableCell>
              <TableCell sx={{ fontSize: 12 }}>{mount.type}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                {mount.accessor ?? '—'}
              </TableCell>
              <TableCell sx={{ fontSize: 12 }}>
                {mount.description || '—'}
              </TableCell>
            </TableRow>
          ))}
          {authMounts.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} align="center">
                No auth methods visible
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function OpenBaoPage() {
  const api = useOpenBaoApi();
  // Read the address once; everything below is written back to it, so every
  // view — namespace, tab, mount, path, secret version, policy — is linkable.
  const initialUrl = useRef(
    parseOpenBaoUrl(new URLSearchParams(window.location.search)),
  ).current;

  const [status, setStatus] = useState<BaoStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [namespace, setNamespace] = useState(initialUrl.ns);
  const [namespaces, setNamespaces] = useState<string[]>([]);
  const [session, setSession] = useState<BaoSession | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [authMounts, setAuthMounts] = useState<BaoAuthMount[] | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [tab, setTab] = useState<OpenBaoTab>(initialUrl.tab);
  const [secretsLocation, setSecretsLocation] = useState<SecretsLocation>({
    mount: initialUrl.mount,
    path: initialUrl.path,
    secret: initialUrl.secret,
    version: initialUrl.version,
  });
  const [policy, setPolicy] = useState(initialUrl.policy);
  const [busy, setBusy] = useState(false);
  const [notification, setNotification] = useState<{
    message: string;
    severity: 'success' | 'error';
  } | null>(null);

  const notify = useCallback(
    (severity: 'success' | 'error', message: string) =>
      setNotification({ severity, message }),
    [],
  );

  const navigateSecrets = useCallback(
    (next: Partial<SecretsLocation>) =>
      setSecretsLocation(current => ({ ...current, ...next })),
    [],
  );

  // ── the URL is the address of this view ──────────────────────────────────
  const urlState = useMemo<OpenBaoUrlState>(
    () => ({
      ns: namespace,
      tab,
      mount: secretsLocation.mount,
      path: secretsLocation.path,
      secret: secretsLocation.secret,
      version: secretsLocation.version,
      policy,
    }),
    [namespace, tab, secretsLocation, policy],
  );

  const adoptUrl = useCallback((next: OpenBaoUrlState) => {
    setNamespace(next.ns);
    setTab(next.tab);
    setSecretsLocation({
      mount: next.mount,
      path: next.path,
      secret: next.secret,
      version: next.version,
    });
    setPolicy(next.policy);
  }, []);

  const { copyShareUrl } = useUrlState<OpenBaoUrlState>({
    state: urlState,
    parse: parseOpenBaoUrl,
    serialize: serializeOpenBaoUrl,
    adopt: adoptUrl,
    navigationKeys: OPENBAO_NAVIGATION_KEYS,
    // Picking the first KV engine because the link named none is the page
    // settling, not the reader navigating.
    isSettling: (previous, next) => !previous.mount && Boolean(next.mount),
  });

  const copyLink = useCallback(
    () => copyShareUrl(message => notify('success', message)),
    [copyShareUrl, notify],
  );

  useEffect(() => {
    api
      .status()
      .then(setStatus)
      .catch(error =>
        setStatusError(error instanceof Error ? error.message : String(error)),
      );
    api
      .namespaces()
      .then(loaded =>
        setNamespaces(loaded.map(entry => entry.replace(/\/$/, ''))),
      )
      .catch(() => setNamespaces([]));
  }, [api]);

  useEffect(() => {
    setSession(null);
    setSessionError(null);
    api
      .session(namespace)
      .then(setSession)
      .catch(error =>
        setSessionError(
          error instanceof Error ? error.message : String(error),
        ),
      );
  }, [api, namespace]);

  useEffect(() => {
    if (tab !== 'access') return;
    setAuthMounts(null);
    setAuthError(null);
    api
      .authMounts(namespace)
      .then(setAuthMounts)
      .catch(error =>
        setAuthError(error instanceof Error ? error.message : String(error)),
      );
  }, [api, namespace, tab]);

  const renew = async () => {
    setBusy(true);
    try {
      await api.renewSession(namespace);
      notify('success', 'Token renewed');
      setSession(await api.session(namespace));
    } catch (error) {
      notify('error', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page themeId="tool">
      <Header
        title="OpenBao"
        subtitle="Secrets management with your Microsoft Entra identity"
      />
      <Content>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ mb: 2, alignItems: { md: 'center' } }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ color: COLORS.text, fontSize: 13, fontWeight: 700 }}
            >
              {status?.clusterName ?? 'OpenBao'}
            </Typography>
            <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
              Signed in as {session?.email ?? 'Microsoft Entra user'}
              {session?.policies.length
                ? ` · ${session.policies.join(', ')}`
                : ''}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            {status && (
              <StatusChip
                label={status.sealed ? 'Sealed' : 'Unsealed'}
                color={status.sealed ? COLORS.red : COLORS.green}
              />
            )}
            {status?.version && (
              <StatusChip label={`v${status.version}`} color={COLORS.blue} />
            )}
            {status?.storageType && (
              <StatusChip label={status.storageType} color={COLORS.muted} />
            )}
            <TextField
              select
              size="small"
              label="Namespace"
              value={namespace}
              onChange={event => setNamespace(event.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">root</MenuItem>
              {namespaces.map(entry => (
                <MenuItem key={entry} value={entry}>
                  {entry}
                </MenuItem>
              ))}
            </TextField>
            <Tooltip title="Copy a link to exactly this view">
              <Button
                variant="outlined"
                size="small"
                startIcon={<LinkIcon />}
                onClick={copyLink}
                sx={{ textTransform: 'none' }}
              >
                Copy link
              </Button>
            </Tooltip>
          </Stack>
        </Stack>

        {statusError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {statusError}
          </Alert>
        )}
        {status?.sealed && (
          <Alert severity="error" sx={{ mb: 2 }}>
            OpenBao is sealed — secrets are unavailable until it is unsealed.
          </Alert>
        )}
        {sessionError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {sessionError}
          </Alert>
        )}

        <Tabs
          value={tab}
          onChange={(_event, value) => setTab(value as OpenBaoTab)}
          sx={{ mb: 2, borderBottom: `1px solid ${COLORS.border}` }}
        >
          <Tab label="Secrets" value="secrets" />
          <Tab label="Policies" value="policies" />
          <Tab label="Access" value="access" />
          <Tab label="Token" value="token" />
        </Tabs>

        {tab === 'secrets' && (
          <SecretsView
            api={api}
            namespace={namespace}
            onNotify={notify}
            location={secretsLocation}
            onNavigate={navigateSecrets}
          />
        )}
        {tab === 'policies' && (
          <PoliciesView
            api={api}
            namespace={namespace}
            onNotify={notify}
            selected={policy}
            onSelect={setPolicy}
          />
        )}
        {tab === 'access' && (
          <AccessView authMounts={authMounts} error={authError} />
        )}
        {tab === 'token' && (
          <Box
            sx={{
              maxWidth: 560,
              p: 2,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 2,
              bgcolor: COLORS.surface,
            }}
          >
            {!session && !sessionError && <CircularProgress size={22} />}
            {session && (
              <>
                <Field label="Identity" value={session.email} />
                <Field label="Display name" value={session.displayName} />
                <Field label="Entity ID" value={session.entityId} />
                <Field
                  label="Policies"
                  value={
                    <Stack
                      direction="row"
                      sx={{ flexWrap: 'wrap', gap: 0.5 }}
                    >
                      {session.policies.map(policy => (
                        <Chip
                          key={policy}
                          size="small"
                          label={policy}
                          sx={{ fontSize: 11 }}
                        />
                      ))}
                    </Stack>
                  }
                />
                <Field
                  label="TTL"
                  value={session.ttl ? `${session.ttl}s` : undefined}
                />
                <Field
                  label="Expires"
                  value={
                    session.expireTime
                      ? new Date(session.expireTime).toLocaleString()
                      : undefined
                  }
                />
                <Field
                  label="Renewable"
                  value={session.renewable ? 'yes' : 'no'}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    busy ? (
                      <CircularProgress size={13} />
                    ) : (
                      <AutorenewIcon />
                    )
                  }
                  disabled={busy || !session.renewable}
                  onClick={renew}
                  sx={{ mt: 1.5, textTransform: 'none' }}
                >
                  Renew token
                </Button>
              </>
            )}
          </Box>
        )}
      </Content>

      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={5000}
        onClose={() => setNotification(null)}
      >
        {notification ? (
          <Alert
            severity={notification.severity}
            onClose={() => setNotification(null)}
          >
            {notification.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Page>
  );
}
